import type { Option } from "../core/option.ts";

/**
 * `StaticRegistry` — a process-wide registry of **built-ins**: populated once
 * at module load (the bootstrap), then frozen, so nothing can change what a
 * wire type or template name means while sheets are alive.
 *
 * The package exports only a read view (`{ get }`) of each registry
 * (`SheetRegistries.BUILTINS`). Registration after bootstrap throws, and
 * nothing — a test included — can empty or refill a registry after it: a
 * test layers its own entries on the client it builds
 * (`SheetRegistries.extend`). The registry objects themselves are module
 * exports, never barrel exports.
 */
export class StaticRegistry<V> {
  readonly #entries = new Map<string, V>();
  #frozen = false;

  readonly #label: string;
  constructor(label: string) {
    this.#label = label;
  }

  /** The entry for `key`; `null` when unregistered. */
  get(key: string): Option<V> {
    return this.#entries.get(key) ?? null;
  }

  /** Bootstrap registration. Throws on a duplicate key or after `freeze`. */
  register(key: string, value: V): void {
    if (this.#frozen) {
      throw new Error(
        `${this.#label}.register: "${key}" after bootstrap — the registry is frozen.`,
      );
    }
    this.#add(key, value);
  }

  /** End of bootstrap: every later `register` throws. */
  freeze(): void {
    this.#frozen = true;
  }

  #add(key: string, value: V): void {
    if (this.#entries.has(key)) {
      throw new Error(`${this.#label}.register: "${key}" already registered.`);
    }
    this.#entries.set(key, value);
  }
}
